import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Upload, FileSpreadsheet, Check, AlertCircle,
  X, RefreshCw, Download, Users, Package, ChevronRight, Info,
  CheckCircle, XCircle, AlertTriangle, HelpCircle
} from '../../icons/wp-icons';
import apiClient from '../../api/apiClient';

// Animation-Varianten
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: 20 }
};

// Quellsysteme mit Logos
const SOURCE_SYSTEMS = [
  {
    id: 'easyfirma',
    name: 'EasyFirma',
    logo: '/logos/easyfirma.svg',
    fallbackColor: '#2563eb',
    fallbackText: 'EF'
  },
  {
    id: 'lexoffice',
    name: 'lexoffice',
    logo: '/logos/lexoffice.svg',
    fallbackColor: '#16a34a',
    fallbackText: 'LO'
  },
  {
    id: 'sevdesk',
    name: 'sevDesk',
    logo: '/logos/sevdesk.svg',
    fallbackColor: '#7c3aed',
    fallbackText: 'SD'
  },
  {
    id: 'bitec',
    name: 'Bitec',
    logo: '/logos/bitec.svg',
    fallbackColor: '#ea580c',
    fallbackText: 'BT'
  },
  {
    id: 'veras',
    name: 'VERAS',
    logo: '/logos/veras.svg',
    fallbackColor: '#ca8a04',
    fallbackText: 'VR'
  },
  {
    id: 'csv',
    name: 'Andere (CSV)',
    logo: null,
    fallbackColor: '#6b7280',
    fallbackText: 'CSV',
    isGeneric: true
  },
  {
    id: 'datanorm',
    name: 'DATANORM',
    logo: '/logos/datanorm.svg',
    fallbackColor: '#0d9488',
    fallbackText: 'DN'
  }
];

// Logo-Komponente mit Fallback
const SystemLogo = ({ system, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base'
  };

  const [imgError, setImgError] = useState(false);

  if (system.logo && !imgError && !system.isGeneric) {
    return (
      <img
        src={system.logo}
        alt={system.name}
        className={`${sizeClasses[size]} object-contain`}
        onError={() => setImgError(true)}
      />
    );
  }

  // Fallback: Farbiger Kreis mit Initialen
  return (
    <div
      className={`${sizeClasses[size]} rounded-lg flex items-center justify-center font-bold text-white`}
      style={{ backgroundColor: system.fallbackColor }}
    >
      {system.fallbackText}
    </div>
  );
};

// Entity-Typen
const ENTITY_TYPES = [
  { id: 'kunde', name: 'Kunden', icon: Users, description: 'Kundenstammdaten importieren' },
  { id: 'katalog', name: 'Artikel/Katalog', icon: Package, description: 'Artikelstamm importieren' }
];

function MigrationWizard({ onClose, onImportComplete, isOnboarding = false, alreadyImported = [] }) {
  // Wizard State
  const [step, setStep] = useState(isOnboarding ? 0 : 1); // Step 0 = Auswahl im Onboarding
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 0: Mehrfachauswahl (nur Onboarding)
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [currentTypeIndex, setCurrentTypeIndex] = useState(0);
  const [completedTypes, setCompletedTypes] = useState([]);

  // Step 1: Quellsystem
  const [sourceSystem, setSourceSystem] = useState(null);
  const [entityType, setEntityType] = useState(null);
  const [instructions, setInstructions] = useState(null);

  // Step 2: Upload
  const [sessionId, setSessionId] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Step 3: Mapping
  const [mappings, setMappings] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);

  // Step 4: Preview & Import
  const [preview, setPreview] = useState(null);
  const [importOptions, setImportOptions] = useState({
    overwriteExisting: false,
    skipDuplicates: true
  });
  const [importResult, setImportResult] = useState(null);

  // Escape-Taste zum Schliessen
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        if (isOnboarding && completedTypes.length > 0 && onImportComplete) {
          onImportComplete(completedTypes);
        }
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [onClose, isOnboarding, completedTypes, onImportComplete]);

  // Lade Anleitungen wenn System gewaehlt
  useEffect(() => {
    if (sourceSystem) {
      loadInstructions(sourceSystem);
    }
  }, [sourceSystem]);

  const loadInstructions = async (system) => {
    try {
      const response = await apiClient.get(`/migration/templates/${system}`);
      setInstructions(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Anleitung:', err);
    }
  };

  // Drag & Drop Handler
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [entityType, sourceSystem]);

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleFileSelect = async (file) => {
    setUploadedFile(file);
    setError('');
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entityType', entityType);
      formData.append('sourceSystem', sourceSystem);

      const response = await apiClient.post('/migration/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSessionId(response.data.sessionId);
      setUploadResult(response.data);
      setMappings(response.data.mappings);
      setAvailableFields(response.data.availableTargetFields);

      // Bei DATANORM direkt zu Schritt 4
      if (response.data.preDefined) {
        setStep(4);
        await loadPreview(response.data.sessionId, response.data.mappings);
      } else {
        setStep(3);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Hochladen');
      setUploadedFile(null);
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async (sid, maps) => {
    setLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/migration/preview', {
        sessionId: sid || sessionId,
        mappings: maps || mappings
      });
      setPreview(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler bei der Vorschau');
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (index, targetField) => {
    const newMappings = [...mappings];
    newMappings[index] = {
      ...newMappings[index],
      targetField: targetField === '_ignore' ? null : targetField,
      ignored: targetField === '_ignore'
    };
    setMappings(newMappings);
  };

  const executeImport = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/migration/execute', {
        sessionId,
        options: importOptions
      });
      setImportResult(response.data);

      // Im Onboarding-Modus: Typ als abgeschlossen markieren
      if (isOnboarding && response.data.success) {
        setCompletedTypes(prev => [...prev, entityType]);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Import');
    } finally {
      setLoading(false);
    }
  };

  // Nächsten Import-Typ starten (Onboarding-Modus)
  const startNextImport = () => {
    const nextIndex = currentTypeIndex + 1;
    if (nextIndex < selectedTypes.length) {
      // Reset für nächsten Import
      setCurrentTypeIndex(nextIndex);
      setEntityType(selectedTypes[nextIndex]);
      setSourceSystem(null);
      setInstructions(null);
      setSessionId(null);
      setUploadedFile(null);
      setUploadResult(null);
      setMappings([]);
      setAvailableFields([]);
      setPreview(null);
      setImportResult(null);
      setStep(1);
    } else {
      // Alle Imports abgeschlossen
      if (onImportComplete) {
        onImportComplete(completedTypes);
      }
      onClose();
    }
  };

  // Schließen mit Callback
  const handleClose = () => {
    if (isOnboarding && completedTypes.length > 0 && onImportComplete) {
      onImportComplete(completedTypes);
    }
    onClose();
  };

  const goBack = () => {
    if (step === 1 && isOnboarding) {
      // Zurück zur Typ-Auswahl im Onboarding
      setStep(0);
      setEntityType(null);
      setSourceSystem(null);
    } else if (step > 1) {
      setStep(step - 1);
      if (step === 4) {
        setPreview(null);
      }
    }
  };

  const goNext = async () => {
    if (step === 0 && selectedTypes.length > 0) {
      // Onboarding: Starte mit erstem ausgewählten Typ
      setEntityType(selectedTypes[0]);
      setCurrentTypeIndex(0);
      setStep(1);
    } else if (step === 1 && sourceSystem && entityType) {
      setStep(2);
    } else if (step === 3) {
      await loadPreview();
      setStep(4);
    }
  };

  const canProceed = () => {
    if (step === 0) return selectedTypes.length > 0;
    if (step === 1) return sourceSystem && entityType;
    if (step === 2) return uploadResult;
    if (step === 3) {
      // Mindestens Pflichtfelder gemapped?
      const requiredFields = availableFields.filter(f => f.required).map(f => f.field);
      const mappedFields = mappings.filter(m => m.targetField).map(m => m.targetField);
      return requiredFields.every(rf => mappedFields.includes(rf));
    }
    return true;
  };

  const getConfidenceIcon = (confidence) => {
    switch (confidence) {
      case 'high':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'medium':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      default:
        return <HelpCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  // Toggle Typ-Auswahl im Onboarding
  const toggleTypeSelection = (typeId) => {
    setSelectedTypes(prev => {
      if (prev.includes(typeId)) {
        return prev.filter(t => t !== typeId);
      } else {
        return [...prev, typeId];
      }
    });
  };

  // Render Step 0 (Onboarding: Mehrfachauswahl)
  const renderStep0 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Was möchten Sie importieren?</h3>
        <p className="text-sm text-text-muted">
          Wählen Sie alle Datentypen aus, die Sie importieren möchten.
          Sie werden nacheinander durch den Import geführt.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {ENTITY_TYPES.filter(type => !alreadyImported.includes(type.id)).map((type) => {
          const Icon = type.icon;
          const isSelected = selectedTypes.includes(type.id);
          return (
            <button
              key={type.id}
              onClick={() => toggleTypeSelection(type.id)}
              className={`p-4 border-2 rounded-lg text-left transition ${
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`relative p-2 rounded-lg ${isSelected ? 'bg-primary/10' : 'bg-gray-100'}`}>
                  <Icon className={`w-6 h-6 ${isSelected ? 'text-primary' : 'text-gray-600'}`} />
                  {isSelected && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="font-medium">{type.name}</div>
                  <div className="text-sm text-text-muted">{type.description}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {alreadyImported.length > 0 && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            <span>
              Bereits importiert: {alreadyImported.map(t =>
                t === 'kunde' ? 'Kunden' : 'Artikel'
              ).join(', ')}
            </span>
          </div>
        </div>
      )}
    </div>
  );

  // Render Steps
  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Entity Type Selection */}
      <div>
        <h3 className="text-lg font-medium mb-3">Was möchten Sie importieren?</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ENTITY_TYPES.map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                onClick={() => setEntityType(type.id)}
                className={`p-4 border-2 rounded-lg text-left transition ${
                  entityType === type.id
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${entityType === type.id ? 'bg-primary/10' : 'bg-gray-100'}`}>
                    <Icon className={`w-5 h-5 ${entityType === type.id ? 'text-primary' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <div className="font-medium">{type.name}</div>
                    <div className="text-sm text-text-muted">{type.description}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Source System Selection */}
      <div>
        <h3 className="text-lg font-medium mb-3">Aus welcher Software importieren Sie?</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {SOURCE_SYSTEMS.filter(sys => {
            // DATANORM nur für Katalog
            if (sys.id === 'datanorm' && entityType !== 'katalog') return false;
            return true;
          }).map((sys) => (
            <button
              key={sys.id}
              onClick={() => setSourceSystem(sys.id)}
              className={`p-4 border-2 rounded-lg text-center transition flex flex-col items-center gap-2 ${
                sourceSystem === sys.id
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <SystemLogo system={sys} size="md" />
              <div className="font-medium text-sm">{sys.name}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Export-Anleitung */}
      {instructions && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
            <Info className="w-4 h-4" />
            So exportieren Sie aus {instructions.name}
          </h4>
          <ol className="list-decimal list-inside space-y-1 text-sm text-blue-700">
            {instructions.steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
          <div className="mt-3 text-xs text-blue-600">
            Unterstützte Formate: {instructions.fileFormats.join(', ')}
          </div>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-3">Datei hochladen</h3>
        <p className="text-sm text-text-muted mb-4">
          Laden Sie Ihre Export-Datei hoch. Unterstützte Formate: CSV, Excel (.xlsx), DATANORM (.001, .WRG)
        </p>
      </div>

      {/* Drag & Drop Zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition ${
          dragActive
            ? 'border-primary bg-primary/5'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input
          type="file"
          accept=".csv,.xlsx,.xls,.001,.wrg,.txt"
          onChange={handleFileInputChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />

        <Upload className={`w-12 h-12 mx-auto mb-4 ${dragActive ? 'text-primary' : 'text-gray-400'}`} />

        <div className="text-lg font-medium mb-1">
          {dragActive ? 'Datei hier ablegen' : 'Datei hierher ziehen'}
        </div>
        <div className="text-sm text-text-muted">
          oder klicken zum Auswählen
        </div>

        {uploadedFile && (
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
            <FileSpreadsheet className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium">{uploadedFile.name}</span>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 text-primary">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Datei wird analysiert...</span>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-1">Felder zuordnen</h3>
        <p className="text-sm text-text-muted">
          Ordnen Sie die Spalten aus Ihrer Datei den WerkPilot-Feldern zu.
          Automatisch erkannte Zuordnungen sind markiert.
        </p>
      </div>

      {/* Mapping Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Spalte in Datei</th>
              <th className="px-4 py-3 text-center w-12"></th>
              <th className="px-4 py-3 text-left font-medium">WerkPilot-Feld</th>
              <th className="px-4 py-3 text-center font-medium w-20">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {mappings.map((mapping, index) => (
              <tr key={index} className={mapping.ignored ? 'bg-gray-50 opacity-60' : ''}>
                <td className="px-4 py-3">
                  <div className="font-medium">{mapping.sourceColumn}</div>
                  {uploadResult?.sampleRows?.[0] && (
                    <div className="text-xs text-text-muted truncate max-w-[200px]">
                      Bsp: {uploadResult.sampleRows[0][mapping.sourceColumn] || '-'}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <ChevronRight className="w-4 h-4 text-gray-400 mx-auto" />
                </td>
                <td className="px-4 py-3">
                  <select
                    value={mapping.targetField || '_ignore'}
                    onChange={(e) => handleMappingChange(index, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="_ignore">-- Ignorieren --</option>
                    {availableFields.map((field) => (
                      <option key={field.field} value={field.field}>
                        {field.label} {field.required ? '*' : ''}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 text-center">
                  {getConfidenceIcon(mapping.confidence)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sample Data Preview */}
      {uploadResult?.sampleRows?.length > 0 && (
        <div>
          <h4 className="font-medium mb-2">Datenvorschau (erste 5 Zeilen)</h4>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  {uploadResult.headers.slice(0, 6).map((header, i) => (
                    <th key={i} className="px-3 py-2 text-left font-medium truncate max-w-[120px]">
                      {header}
                    </th>
                  ))}
                  {uploadResult.headers.length > 6 && (
                    <th className="px-3 py-2 text-left font-medium">...</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {uploadResult.sampleRows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {uploadResult.headers.slice(0, 6).map((header, cellIndex) => (
                      <td key={cellIndex} className="px-3 py-2 truncate max-w-[120px]">
                        {row[header] || '-'}
                      </td>
                    ))}
                    {uploadResult.headers.length > 6 && (
                      <td className="px-3 py-2 text-text-muted">...</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      {!importResult ? (
        <>
          <div>
            <h3 className="text-lg font-medium mb-1">Vorschau & Import</h3>
            <p className="text-sm text-text-muted">
              Prüfen Sie die Zusammenfassung und starten Sie den Import.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8 gap-2 text-primary">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span>Vorschau wird erstellt...</span>
            </div>
          ) : preview && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-2xl font-bold text-green-700">{preview.newRecords}</div>
                  <div className="text-sm text-green-600">Neue Einträge</div>
                </div>
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-700">{preview.duplicates}</div>
                  <div className="text-sm text-yellow-600">Duplikate gefunden</div>
                </div>
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-2xl font-bold text-blue-700">{preview.totalRecords}</div>
                  <div className="text-sm text-blue-600">Gesamt in Datei</div>
                </div>
              </div>

              {/* Duplicate Details */}
              {preview.duplicateDetails?.length > 0 && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-medium text-yellow-800 mb-2">Erkannte Duplikate</h4>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    {preview.duplicateDetails.slice(0, 5).map((dup, i) => (
                      <li key={i}>
                        {dup.importRecord.name || dup.importRecord.bezeichnung || dup.importRecord.email}
                        {' '}<span className="text-yellow-600">(existiert bereits)</span>
                      </li>
                    ))}
                    {preview.duplicateDetails.length > 5 && (
                      <li className="text-yellow-600">... und {preview.duplicateDetails.length - 5} weitere</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Validation Errors */}
              {preview.validationErrors?.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="font-medium text-red-800 mb-2">Validierungsprobleme</h4>
                  <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                    {preview.validationErrors.slice(0, 10).map((err, i) => (
                      <li key={i} className="flex items-start gap-2">
                        {err.severity === 'error' ? (
                          <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        )}
                        Zeile {err.row}: {err.message}
                      </li>
                    ))}
                    {preview.validationErrors.length > 10 && (
                      <li className="text-red-600">... und {preview.validationErrors.length - 10} weitere</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Import Options */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <h4 className="font-medium">Import-Optionen</h4>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="duplicateHandling"
                    checked={importOptions.skipDuplicates && !importOptions.overwriteExisting}
                    onChange={() => setImportOptions({ skipDuplicates: true, overwriteExisting: false })}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-sm">Duplikate überspringen</div>
                    <div className="text-xs text-text-muted">Bestehende Einträge bleiben unverändert</div>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="duplicateHandling"
                    checked={importOptions.overwriteExisting}
                    onChange={() => setImportOptions({ skipDuplicates: false, overwriteExisting: true })}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-sm">Duplikate aktualisieren</div>
                    <div className="text-xs text-text-muted">Bestehende Einträge werden mit neuen Daten überschrieben</div>
                  </div>
                </label>
              </div>

              {/* Sample Records */}
              {preview.sampleRecords?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Beispiel-Datensätze</h4>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          {Object.keys(preview.sampleRecords[0]).slice(0, 5).map((key) => (
                            <th key={key} className="px-3 py-2 text-left font-medium">{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {preview.sampleRecords.slice(0, 3).map((record, i) => (
                          <tr key={i}>
                            {Object.keys(record).slice(0, 5).map((key) => (
                              <td key={key} className="px-3 py-2 truncate max-w-[150px]">
                                {record[key] || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        /* Import Result */
        <div className="text-center py-8">
          {importResult.success ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-green-800 mb-2">Import erfolgreich!</h3>
              <p className="text-text-muted mb-6">
                Ihre Daten wurden erfolgreich importiert.
              </p>

              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-6">
                <div className="p-3 bg-green-50 rounded-lg">
                  <div className="text-xl font-bold text-green-700">{importResult.created}</div>
                  <div className="text-xs text-green-600">Erstellt</div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="text-xl font-bold text-blue-700">{importResult.updated}</div>
                  <div className="text-xs text-blue-600">Aktualisiert</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xl font-bold text-gray-700">{importResult.skipped}</div>
                  <div className="text-xs text-gray-600">Übersprungen</div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-red-800 mb-2">Import fehlgeschlagen</h3>
              <p className="text-text-muted mb-4">
                Beim Import sind Fehler aufgetreten.
              </p>

              {importResult.errors?.length > 0 && (
                <div className="text-left max-w-md mx-auto p-4 bg-red-50 border border-red-200 rounded-lg">
                  <ul className="text-sm text-red-700 space-y-1">
                    {importResult.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>{err.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {isOnboarding && currentTypeIndex < selectedTypes.length - 1 ? (
            <button
              onClick={startNextImport}
              className="mt-6 px-6 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition flex items-center gap-2 mx-auto"
            >
              Weiter: {selectedTypes[currentTypeIndex + 1] === 'kunde' ? 'Kunden' : 'Artikel'} importieren
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleClose}
              className="mt-6 px-6 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition"
            >
              {isOnboarding ? 'Fertig' : 'Schliessen'}
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Animated Backdrop */}
        <motion.div
          className="fixed inset-0 bg-black/50"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={{ duration: 0.2, ease: "easeOut" }}
          onClick={handleClose}
        />

        {/* Modal Container */}
        <div className="flex min-h-full items-center justify-center p-4">
          {/* Animated Modal Content */}
          <motion.div
            className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold">Daten importieren</h2>
            <p className="text-sm text-text-muted">Wechsel-Assistent</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-100">
          {/* Multi-Import-Anzeige im Onboarding */}
          {isOnboarding && selectedTypes.length > 1 && step > 0 && (
            <div className="mb-3 text-center">
              <span className="text-sm text-text-muted">
                Import {currentTypeIndex + 1} von {selectedTypes.length}:{' '}
                <span className="font-medium text-primary">
                  {entityType === 'kunde' ? 'Kunden' : 'Artikel/Katalog'}
                </span>
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            {(isOnboarding && step === 0 ? ['Auswahl'] : ['Quelle wählen', 'Datei hochladen', 'Felder zuordnen', 'Importieren']).map((label, i) => {
              const stepNum = isOnboarding && step === 0 ? 0 : i + 1;
              const isActive = step === stepNum;
              const isCompleted = step > stepNum;
              return (
                <div key={i} className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                    isCompleted
                      ? 'bg-green-100 text-green-700'
                      : isActive
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-500'
                  }`}>
                    {isCompleted ? <Check className="w-4 h-4" /> : (isOnboarding && step === 0 ? 1 : i + 1)}
                  </div>
                  <span className={`ml-2 text-sm hidden sm:inline ${
                    isActive ? 'font-medium' : 'text-text-muted'
                  }`}>
                    {label}
                  </span>
                  {i < (isOnboarding && step === 0 ? 0 : 3) && (
                    <div className={`w-8 sm:w-16 h-0.5 mx-2 ${
                      isCompleted ? 'bg-green-300' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
              <button onClick={() => setError('')} className="ml-auto">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {step === 0 && renderStep0()}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </div>

        {/* Footer */}
        {!importResult && (
          <div className="flex items-center justify-between p-6 border-t border-gray-200">
            <button
              onClick={(step === 0 || (step === 1 && !isOnboarding)) ? handleClose : goBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition"
            >
              <ArrowLeft className="w-4 h-4" />
              {(step === 0 || (step === 1 && !isOnboarding)) ? 'Abbrechen' : 'Zurück'}
            </button>

            {step < 4 ? (
              <button
                onClick={goNext}
                disabled={!canProceed() || loading}
                className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50"
              >
                Weiter
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={executeImport}
                disabled={loading || preview?.hasErrors}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-button font-medium hover:bg-green-700 transition disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Importieren...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Import starten
                  </>
                )}
              </button>
            )}
          </div>
        )}
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
}

export default MigrationWizard;
